import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useFirehoseFilterStore } from "@/widget-components/firehose-widgets/shared/FilterStore";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";
import { useEffect, useRef } from "react";
import { BackendSource } from ".";
import { isNotificationMethodEnabled } from "@/types/notifications";

type PortfoliosBySource = Record<BackendSource | "allStories", Portfolio[]>;
type AlertMethod = "sound" | "popup";

/**
 * Per-source watchlists selected by the current Watchlist Alert Mode
 * (off/highlight/filter/both), before any method (sound/popup) filter. Shared
 * by the sound and pop-up selectors so the alert-mode logic lives in one place.
 */
function useAlertModePortfoliosBySource(): PortfoliosBySource {
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistAlertMode } = useWatchlistAlertMode();

  const highlightedPortfolios: Record<
    BackendSource | "allStories",
    number[] | undefined
  > = useFirehoseFilterStore((state) => ({
    allStories: state.allStories.portfoliosHighlight,
    "crypto-web-news": state["crypto-web-news"].portfoliosHighlight,
    "energy-web-news": state["energy-web-news"].portfoliosHighlight,
    "stocks-one": state["stocks-one"].portfoliosHighlight,
    "stocks-two": state["stocks-two"].portfoliosHighlight,
    "stocks-three": state["stocks-three"].portfoliosHighlight,
    "fin-web-news": state["fin-web-news"].portfoliosHighlight,
    "truth-social": state["truth-social"].portfoliosHighlight,
    "fin-x": state["fin-x"].portfoliosHighlight,
    "energy-x": state["energy-x"].portfoliosHighlight,
    "stocks-news": state["stocks-news"].portfoliosHighlight,
    "stocks-four": state["stocks-four"]?.portfoliosHighlight,
    "stocks-four-fedspeak": state["stocks-four-fedspeak"]?.portfoliosHighlight,
    "stocks-four-filings": state["stocks-four-filings"]?.portfoliosHighlight,
    "stocks-four-general": state["stocks-four-general"]?.portfoliosHighlight,
    "newsquawk-sec-filings":
      state["newsquawk-sec-filings"]?.portfoliosHighlight,
    "stocks-five-eu": state["stocks-five-eu"]?.portfoliosHighlight,
  }));

  const filteredPortfolios: Record<
    BackendSource | "allStories",
    number[] | undefined
  > = useFirehoseFilterStore((state) => ({
    allStories: state.allStories.portfoliosFilter,
    "crypto-web-news": state["crypto-web-news"].portfoliosFilter,
    "energy-web-news": state["energy-web-news"].portfoliosFilter,
    "stocks-one": state["stocks-one"].portfoliosFilter,
    "stocks-two": state["stocks-two"].portfoliosFilter,
    "stocks-three": state["stocks-three"].portfoliosFilter,
    "fin-web-news": state["fin-web-news"].portfoliosFilter,
    "truth-social": state["truth-social"].portfoliosFilter,
    "fin-x": state["fin-x"].portfoliosFilter,
    "energy-x": state["energy-x"].portfoliosFilter,
    "stocks-news": state["stocks-news"].portfoliosFilter,
    "stocks-four": state["stocks-four"]?.portfoliosFilter,
    "stocks-four-fedspeak": state["stocks-four-fedspeak"]?.portfoliosFilter,
    "stocks-four-filings": state["stocks-four-filings"]?.portfoliosFilter,
    "stocks-four-general": state["stocks-four-general"]?.portfoliosFilter,
    "newsquawk-sec-filings": state["newsquawk-sec-filings"]?.portfoliosFilter,
    "stocks-five-eu": state["stocks-five-eu"]?.portfoliosFilter,
  }));

  return (
    Object.keys(highlightedPortfolios) as Array<BackendSource | "allStories">
  ).reduce((acc, source) => {
    // If mode is "off", return empty array
    if (watchlistAlertMode === "off") {
      acc[source] = [];
      return acc;
    }

    // Get portfolio IDs based on mode
    const highlightIds = highlightedPortfolios[source] ?? [];
    const filterIds = filteredPortfolios[source] ?? [];

    let portfolioIds: number[] = [];
    if (watchlistAlertMode === "highlight") {
      portfolioIds = highlightIds;
    } else if (watchlistAlertMode === "filter") {
      portfolioIds = filterIds;
    } else if (watchlistAlertMode === "both") {
      // Combine and deduplicate
      portfolioIds = [...new Set([...highlightIds, ...filterIds])];
    }

    // Map ids to portfolios (method filtering happens in the callers)
    acc[source] = portfolioIds
      .map((portfolioId) =>
        portfolios.find((portfolio) => portfolio.id === portfolioId)
      )
      .filter((portfolio): portfolio is Portfolio => portfolio !== undefined);

    return acc;
  }, {} as PortfoliosBySource);
}

/**
 * Returns a ref of alert-mode watchlists that have the given notification
 * method enabled, per source. Ref so SSE/ingest callbacks read current values
 * without re-rendering.
 */
function useMethodEnabledRef(method: AlertMethod) {
  const bySource = useAlertModePortfoliosBySource();

  const filtered = (
    Object.keys(bySource) as Array<BackendSource | "allStories">
  ).reduce((acc, source) => {
    acc[source] = bySource[source].filter((portfolio) =>
      isNotificationMethodEnabled(portfolio.notifications, method)
    );
    return acc;
  }, {} as PortfoliosBySource);

  const ref = useRef(filtered);
  useEffect(() => {
    ref.current = filtered;
  }, [filtered]);

  return ref;
}

export function usePortfoliosWithSoundEnabled() {
  return useMethodEnabledRef("sound");
}

export function usePortfoliosWithPopupEnabled() {
  return useMethodEnabledRef("popup");
}
